import { useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format, startOfWeek, subWeeks, parseISO } from 'date-fns'
import { Sparkles, ShoppingCart, ChefHat, Calendar, TrendingUp, Store as StoreIcon } from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { estimateWeeklyWaste } from '../lib/wasteCalculator'
import type { WasteItem } from '../lib/wasteCalculator'
import PremiumGate from '../components/shared/PremiumGate'
import { calculateRestockSummary, isShoppingEve, isShoppingDay } from '../lib/restockCalculator'
import {
  analyzeStorePrices,
  calculateSavingsOpportunities,
} from '../lib/storePriceAnalyzer'
import { useBudget } from '../hooks/useBudget'
import BudgetCard from '../components/savings/BudgetCard'
import WasteInsight from '../components/savings/WasteInsight'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PantryRow {
  id:                     string
  name:                   string
  emoji:                  string
  freshness_category:     string
  
  avg_restock_cycle_days: number | null
}

interface WeeklySpend {
  week:  string
  total: number
}

interface ExpirationEvent {
  id:                 string
  item_id:            string
  used_before_expiry: boolean | null
  wasted_dollars:     number | null
  price_paid:         number | null
  expiration_date:    string
  confirmed_at:       string | null
  pantry_items:       { name: string; emoji: string } | null
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`rounded-[18px] border border-border-sand bg-paper animate-pulse ${className}`} />
  )
}

// ─── Recommendation labels ────────────────────────────────────────────────────

const REC: Record<'buy_more_often' | 'freeze' | 'buy_less', string> = {
  buy_more_often: 'Buy more frequently',
  freeze:         'Consider freezing',
  buy_less:       'Buy smaller amounts',
}

// ─── Section 1 — Hero ────────────────────────────────────────────────────────

function HeroSection({
  totalSaved,
  hasData,
  isLoading,
}: {
  totalSaved: number
  hasData:    boolean
  isLoading:  boolean
}) {
  if (isLoading) return <Skeleton className="h-32" />

  if (!hasData) {
    return (
      <div className="rounded-[18px] border border-border-sand bg-paper p-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
          Savings to date
        </div>
        <p className="font-serif text-[18px] font-medium text-ink leading-[1.2]">
          Start <em className="italic">tracking expiry</em> to see savings.
        </p>
        <p className="font-serif text-[13px] italic text-ink-muted mt-2 max-w-[260px] mx-auto leading-[1.4]">
          Mark items as used before they expire to build your total.
        </p>
      </div>
    )
  }

  if (totalSaved === 0) {
    return (
      <div className="rounded-[18px] border border-border-sand bg-paper p-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
          Savings to date
        </div>
        <div className="font-serif text-[48px] font-medium text-ink-muted/60 leading-none tabular-nums">
          $0.00
        </div>
        <p className="font-serif text-[13px] italic text-ink-muted mt-3 leading-[1.4]">
          Use items before they expire to build your savings.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-[18px] border border-border-sand bg-ok-bg p-6 text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-forest mb-2">
        Savings to date
      </div>
      <div className="font-serif text-[48px] font-medium text-forest leading-none tabular-nums">
        ${totalSaved.toFixed(2)}
      </div>
      <p className="font-serif text-[13px] italic text-forest/80 mt-3 leading-[1.4]">
        saved by using items before they expired
      </p>
    </div>
  )
}

// ─── Section 2 — Spend trend chart ───────────────────────────────────────────

function SpendTrendSection({
  data,
  isLoading,
}: {
  data:      WeeklySpend[]
  isLoading: boolean
}) {
  const hasSpend = data.some(d => d.total > 0)

  return (
    <div className="rounded-[18px] border border-border-sand bg-paper p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-4">
        Spend trend
      </div>

      {isLoading ? (
        <div className="h-[180px] flex items-center justify-center">
          <div className="h-6 w-6 rounded-full border-2 border-forest border-t-transparent animate-spin" />
        </div>
      ) : !hasSpend ? (
        <div className="h-[180px] flex items-center justify-center">
          <p className="font-serif text-[13px] italic text-ink-muted text-center px-4">
            Start logging prices to track spending.
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0E8D8" />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 10, fill: '#9A8B73' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9A8B73' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `$${v}`}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 10,
                border: '1px solid #E8DEC8',
                background: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(43, 36, 32, 0.06)',
              }}
              formatter={(v: unknown) => [`$${(v as number).toFixed(2)}`, 'Spend']}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#2B4A3A"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, fill: '#2B4A3A', strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ─── Section 3 — Weekly waste breakdown ──────────────────────────────────────

function WasteBreakdownSection({
  items,
  isLoading,
}: {
  items:     WasteItem[]
  isLoading: boolean
}) {
  const estimates = useMemo(() => estimateWeeklyWaste(items).slice(0, 3), [items])

  if (isLoading) return <Skeleton className="h-48" />

  return (
    <div className="rounded-[18px] border border-border-sand bg-paper p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-4">
        Weekly waste
      </div>

      {estimates.length === 0 ? (
        <div className="py-2 text-center">
          <p className="font-serif text-[15px] font-medium text-ink">
            Nothing <em className="italic">wasted</em> this week.
          </p>
          <p className="font-serif text-[12px] italic text-ink-muted mt-1">
            Keep it up.
          </p>
        </div>
      ) : (
        <div className="rounded-[14px] border border-border-sand overflow-hidden">
          {estimates.map((est, idx) => (
            <div
              key={est.itemId}
              className={`flex items-center gap-3 px-4 py-3 bg-cream-warm ${
                idx !== estimates.length - 1 ? 'border-b border-border-hairline' : ''
              }`}
            >
              <span className="text-[24px] leading-none flex-shrink-0">{est.itemEmoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em] truncate">
                  {est.itemName}
                </p>
                <div className="text-[10px] uppercase tracking-[0.12em] font-medium text-terracotta mt-0.5">
                  {REC[est.recommendation]}
                </div>
              </div>
              <p className="font-serif text-[14px] font-medium text-terracotta flex-shrink-0 tabular-nums">
                ~${est.estimatedWasteDollars.toFixed(2)}/wk
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Section 4 — Annual projection ───────────────────────────────────────────

function AnnualProjectionSection({
  annualProjection,
  totalTracked,
  isLoading,
}: {
  annualProjection: number
  totalTracked:     number
  isLoading:        boolean
}) {
  if (isLoading) return <Skeleton className="h-20" />

  return (
    <div className="rounded-[18px] border border-border-sand bg-paper p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
        Annual projection
      </div>

      {annualProjection > 0 ? (
        <div>
          <p className="font-serif text-[16px] text-ink leading-[1.3]">
            On track to waste <span className="font-medium text-terracotta">${Math.round(annualProjection)}</span> this year.
          </p>
          <p className="font-serif text-[12px] italic text-ink-muted mt-1.5">
            Based on your expiry history.
          </p>
        </div>
      ) : totalTracked === 0 ? (
        <p className="font-serif text-[14px] italic text-ink-muted text-center py-2">
          Add expiration dates to items to see your annual waste projection.
        </p>
      ) : (
        <p className="font-serif text-[15px] font-medium text-forest text-center py-2">
          Great job — no waste tracked <em className="italic">yet</em>.
        </p>
      )}
    </div>
  )
}

// ─── Savings page ─────────────────────────────────────────────────────────────

export default function Savings() {
  const { session } = useAuth()
  const navigate    = useNavigate()
  const userId      = session?.user.id

  useEffect(() => { document.title = 'Savings — Pantry' }, [])

  // ── Pantry + flag ───────────────────────────────────────────────────────────

  const { data: userData } = useQuery({
    queryKey: ['userMeta', userId],
    enabled:  !!userId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('household_id, is_premium')
        .eq('id', userId!)
        .single()
      if (error) throw error
      return data as { household_id: string; is_premium: boolean }
    },
  })

  const householdId = userData?.household_id ?? null
  const isPremium   = userData?.is_premium ?? false

  // ── Budget ────────────────────────────────────────────────────────────────

  const {
    budget,
    summary:              budgetSummary,
    period:               budgetPeriod,
    lastMonthSpend,
    monthOverMonthDelta,
    setBudget,
  } = useBudget(householdId)

  // ── Household (shopping day) ──────────────────────────────────────────────

  const { data: householdRow } = useQuery({
    queryKey: ['household', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('households')
        .select('shopping_day')
        .eq('id', householdId!)
        .single()
      return data
    },
  })

  const shoppingDay = householdRow?.shopping_day ?? 6
  const isEve       = isShoppingEve(shoppingDay)
  const isToday     = isShoppingDay(shoppingDay)

  // ── Restock items + confirmations (premium only) ───────────────────────────

  const { data: restockItems } = useQuery({
    queryKey: ['restockItems', householdId],
    enabled:  !!householdId && isPremium,
    queryFn:  async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, emoji, is_active, last_confirmed_at, avg_restock_cycle_days, confirmation_count, status_override')
        .eq('household_id', householdId!)
        .eq('is_active', true)
      return data ?? []
    },
  })

  const { data: restockConfirmations } = useQuery({
    queryKey: ['restockConfirmations', householdId],
    enabled:  !!householdId && isPremium,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, price_paid')
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
      return data ?? []
    },
  })

  const restockSummary = useMemo(() => {
    if (!restockItems || !restockConfirmations) return null
    return calculateRestockSummary(restockItems, restockConfirmations)
  }, [restockItems, restockConfirmations])

  // ── Store prices ──────────────────────────────────────────────────────────

  // ── All price confirmations (for price history card) ─────────────────────

  const { data: allPriceConfs = [] } = useQuery({
    queryKey: ['all-price-history', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, price_paid, confirmed_at, pantry_items(name, emoji)')
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: true })
      return data ?? []
    },
  })

  const priceHistoryStats = useMemo(() => {
    const grouped: Record<string, { name: string; emoji: string; prices: number[]; oldest: number; latest: number }> = {}
    for (const c of allPriceConfs) {
      const id = c.item_id
      if (!grouped[id]) {
        const item = (Array.isArray(c.pantry_items) ? c.pantry_items[0] : c.pantry_items) as { name: string; emoji: string } | null
        grouped[id] = { name: item?.name ?? '', emoji: item?.emoji ?? '📦', prices: [], oldest: 0, latest: 0 }
      }
      grouped[id].prices.push(c.price_paid as number)
    }
    // Only items with 2+ records
    const items = Object.entries(grouped)
      .filter(([, v]) => v.prices.length >= 2)
      .map(([id, v]) => {
        const oldest = v.prices[0]
        const latest = v.prices[v.prices.length - 1]
        const pct    = oldest > 0 ? ((latest - oldest) / oldest) * 100 : 0
        return { id, name: v.name, emoji: v.emoji, count: v.prices.length, pct }
      })
    const rising        = items.filter(i => i.pct > 1)
    const falling       = items.filter(i => i.pct < -1)
    const biggestIncrease = rising.sort((a, b) => b.pct - a.pct)[0] ?? null
    return { items, risingCount: rising.length, fallingCount: falling.length, biggestIncrease }
  }, [allPriceConfs])

  // ── Store prices ──────────────────────────────────────────────────────────

  const { data: storePricesData = [] } = useQuery({
    queryKey: ['store-prices', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('store_prices')
        .select('id, item_id, store_name, price, recorded_at, pantry_items(name, emoji)')
        .eq('household_id', householdId!)
        .order('recorded_at', { ascending: false })
      return data ?? []
    },
  })

  const { data: storeConfirmations = [] } = useQuery({
    queryKey: ['store-confirmations', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, store_name, confirmed_at')
        .eq('household_id', householdId!)
        .not('store_name', 'is', null)
        .order('confirmed_at', { ascending: false })
        .limit(200)
      return data ?? []
    },
  })

  const storePriceComparisons = useMemo(
    () => analyzeStorePrices(storePricesData),
    [storePricesData],
  )
  const storeOpportunities = useMemo(
    () => calculateSavingsOpportunities(storePriceComparisons, storeConfirmations),
    [storePriceComparisons, storeConfirmations],
  )
  const totalStoreSaving = storeOpportunities.reduce((s, o) => s + o.potentialMonthlySaving, 0)
  const topOpportunity   = storeOpportunities[0] ?? null

  // ── Query 1 — pantry items with avg_price from confirmations ───────────────

  const { data: pantryItems, isLoading: pantryLoading } = useQuery({
    queryKey: ['savingsPantry', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      // Fetch pantry items
      const { data: rows, error: rowsErr } = await supabase
        .from('pantry_items')
        .select('id, name, emoji, freshness_category, avg_restock_cycle_days')
        .eq('household_id', householdId!)
        .eq('is_active', true)
      if (rowsErr) throw rowsErr

      // Fetch price history for these items (price_paid not null, newest first)
      const itemIds = (rows ?? []).map(r => r.id)
      if (itemIds.length === 0) return [] as WasteItem[]

      const { data: confs, error: confsErr } = await supabase
        .from('confirmations')
        .select('item_id, price_paid, confirmed_at')
        .in('item_id', itemIds)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: false })
      if (confsErr) throw confsErr

      // Group confirmations by item_id; take last 3 per item
      const priceMap: Record<string, number[]> = {}
      for (const c of confs ?? []) {
        if (!priceMap[c.item_id]) priceMap[c.item_id] = []
        if (priceMap[c.item_id].length < 3) {
          priceMap[c.item_id].push(c.price_paid as number)
        }
      }

      // Build WasteItem array with computed avg_price
      return (rows as PantryRow[]).map<WasteItem>(row => {
        const prices   = priceMap[row.id] ?? []
        const avgPrice = prices.length > 0
          ? prices.reduce((s, p) => s + p, 0) / prices.length
          : null
        return { ...row, avg_price: avgPrice }
      })
    },
  })

  // ── Query 2 — weekly spend (last 8 weeks) ──────────────────────────────────

  const { data: weeklySpend, isLoading: spendLoading } = useQuery({
    queryKey: ['weeklySpend', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const since = subWeeks(new Date(), 8).toISOString()

      const { data: confs, error } = await supabase
        .from('confirmations')
        .select('confirmed_at, price_paid')
        .eq('household_id', householdId!)
        .gte('confirmed_at', since)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: true })
      if (error) throw error

      // Build 8 week buckets (oldest → newest)
      const now    = new Date()
      const weeks: WeeklySpend[] = Array.from({ length: 8 }, (_, i) => ({
        week:  format(startOfWeek(subWeeks(now, 7 - i)), 'MMM d'),
        total: 0,
      }))

      for (const c of confs ?? []) {
        const weekLabel = format(startOfWeek(parseISO(c.confirmed_at)), 'MMM d')
        const bucket    = weeks.find(w => w.week === weekLabel)
        if (bucket) bucket.total = parseFloat((bucket.total + (c.price_paid as number)).toFixed(2))
      }

      return weeks
    },
  })

  // ── Query 2b — expiration events (confirmed only) ────────────────────────

  const { data: expirationData, isLoading: eventsLoading } = useQuery({
    queryKey: ['expiration-waste', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      if (!householdId) return []

      const { data: events } = await supabase
        .from('expiration_events')
        .select(`
          id,
          expiration_date,
          expired,
          used_before_expiry,
          wasted_dollars,
          price_paid,
          confirmed_at,
          item_id,
          pantry_items(name, emoji)
        `)
        .eq('household_id', householdId)
        .not('confirmed_at', 'is', null)
        .order('expiration_date', { ascending: false })

      if (!events || events.length === 0) return []

      // For events missing price_paid, look it up from the confirmations table
      const eventsWithPrices = await Promise.all(
        events.map(async (event) => {
          if (event.price_paid) return event

          const { data: confirmation } = await supabase
            .from('confirmations')
            .select('price_paid')
            .eq('item_id', event.item_id)
            .not('price_paid', 'is', null)
            .order('confirmed_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          return {
            ...event,
            price_paid: confirmation?.price_paid ?? 0,
          }
        })
      )

      return eventsWithPrices as unknown as ExpirationEvent[]
    },
  })

  // ── Real waste derived from expiration_events ─────────────────────────────

  const realWasteData = useMemo(() => {
    if (!expirationData) return {
      totalWasted: 0, totalUsed: 0, totalTracked: 0,
      wastedItems: [] as ExpirationEvent[], usedItems: [] as ExpirationEvent[], worstItem: null as string | null,
    }

    const wasted = expirationData.filter(e => e.used_before_expiry === false && e.confirmed_at !== null)
    const used   = expirationData.filter(e => e.used_before_expiry === true)

    const totalWasted = wasted.reduce(
      (sum, e) => sum + (e.wasted_dollars || e.price_paid || 0), 0
    )

    const itemExpiredCount: Record<string, number> = {}
    wasted.forEach(e => {
      const name = e.pantry_items?.name ?? 'Unknown'
      itemExpiredCount[name] = (itemExpiredCount[name] ?? 0) + 1
    })
    const worstItem = Object.entries(itemExpiredCount)
      .sort((a, b) => b[1] - a[1])[0]?.[0] ?? null

    return {
      totalWasted,
      totalUsed:    used.length,
      totalTracked: expirationData.length,
      wastedItems:  wasted,
      usedItems:    used,
      worstItem,
    }
  }, [expirationData])

  // ── Annual projection from real expiry data ───────────────────────────────

  const annualProjection = useMemo(() => {
    if (!expirationData || expirationData.length === 0) return 0
    if (realWasteData.totalWasted === 0) return 0

    const earliest = expirationData
      .filter(e => e.confirmed_at)
      .sort((a, b) =>
        new Date(a.confirmed_at!).getTime() - new Date(b.confirmed_at!).getTime()
      )[0]

    if (!earliest) return realWasteData.totalWasted * 52

    const weeksElapsed = Math.max(1,
      (Date.now() - new Date(earliest.confirmed_at!).getTime()) /
      (1000 * 60 * 60 * 24 * 7)
    )

    return (realWasteData.totalWasted / weeksElapsed) * 52
  }, [expirationData, realWasteData])

  // ── Derived savings and waste numbers ────────────────────────────────────

  const totalSaved = useMemo(() => {
    if (!expirationData || expirationData.length === 0) return 0
    const result = expirationData
      .filter(e => e.used_before_expiry === true)
      .reduce((sum, e) => sum + (e.price_paid || 0), 0)
    return result
  }, [expirationData])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-[100dvh] bg-cream pb-28">

      {/* Masthead */}
      <header className="px-4 pt-6 pb-5">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
          Insights
        </div>
        <h1 className="font-serif text-[28px] font-normal leading-[1.05] tracking-[-0.015em] text-ink">
          Your <em className="italic">savings</em>
        </h1>
        <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.4]">
          Waste, budget, and how Pantry pays for itself.
        </p>
      </header>

      <div className="px-4 flex flex-col gap-4">

        {/* Hero — always visible */}
        <HeroSection
          totalSaved={totalSaved}
          hasData={realWasteData.totalTracked > 0}
          isLoading={eventsLoading}
        />

        <PremiumGate isPremium={isPremium}>
          <div className="flex flex-col gap-4">

            {/* Upcoming shopping */}
            {(isEve || isToday) && restockSummary && (
              <div className="rounded-[18px] border border-border-sand bg-paper p-5">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart className="h-[14px] w-[14px] text-forest" strokeWidth={1.8} />
                  <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                    {isToday ? 'Shopping today' : 'Shopping tomorrow'}
                  </div>
                </div>

                <div className="flex gap-8 mb-4">
                  <div>
                    <div className="font-serif text-[28px] font-medium text-ink leading-none">
                      {restockSummary.itemCount}
                    </div>
                    <div className="font-serif text-[12px] italic text-ink-muted mt-1">
                      items needed
                    </div>
                  </div>
                  {restockSummary.estimatedTotal > 0 && (
                    <div>
                      <div className="font-serif text-[28px] font-medium text-ink leading-none tabular-nums">
                        ${restockSummary.estimatedTotal.toFixed(0)}
                      </div>
                      <div className="font-serif text-[12px] italic text-ink-muted mt-1">
                        estimated
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate('/list')}
                  className="w-full rounded-[14px] bg-forest text-forest-ink py-3 text-[14px] font-bold tracking-[-0.01em]"
                >
                  See your list
                </button>
              </div>
            )}

            <SpendTrendSection data={weeklySpend ?? []} isLoading={spendLoading} />
            <WasteBreakdownSection items={pantryItems ?? []} isLoading={pantryLoading} />

            {/* AI Waste Insight */}
            <WasteInsight
              householdId={householdId ?? ''}
              householdSize={2}
              isPremium={isPremium}
            />

            {/* AI Meal Planning */}
            <div className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-[14px] w-[14px] text-forest" strokeWidth={1.8} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  AI Meal Planning
                </div>
              </div>
              <p className="font-serif text-[14px] italic text-ink-muted mb-4 leading-[1.4]">
                Meal suggestions from what's expiring this week.
              </p>
              <button
                onClick={() => navigate('/meal-planning')}
                className="w-full rounded-[14px] border border-border-sand bg-cream py-2.5 text-[13px] font-semibold text-forest"
              >
                Plan meals this week →
              </button>
            </div>

            {/* Expiry tracking */}
            <div className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-4">
                Expiry tracking
              </div>

              {eventsLoading ? (
                <div className="h-20 flex items-center justify-center">
                  <div className="h-6 w-6 rounded-full border-2 border-forest border-t-transparent animate-spin" />
                </div>
              ) : realWasteData.totalTracked > 0 ? (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-serif text-[13px] italic text-ink-muted">
                      Items tracked with expiry dates
                    </span>
                    <span className="font-serif text-[16px] font-medium text-ink tabular-nums">
                      {realWasteData.totalTracked}
                    </span>
                  </div>

                  <div className="flex h-[6px] overflow-hidden rounded-full bg-border-hairline mb-2">
                    <div
                      className="bg-forest transition-all"
                      style={{ width: `${(realWasteData.totalUsed / realWasteData.totalTracked) * 100}%` }}
                    />
                    <div
                      className="bg-terracotta transition-all"
                      style={{ width: `${(realWasteData.wastedItems.length / realWasteData.totalTracked) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] italic font-serif mb-4">
                    <span className="text-forest">Used: {realWasteData.totalUsed}</span>
                    <span className="text-terracotta">Wasted: {realWasteData.wastedItems.length}</span>
                  </div>

                  {realWasteData.totalWasted > 0 && (
                    <div className="rounded-[12px] bg-critical-bg border border-critical/20 p-4 mb-3">
                      <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-critical mb-1">
                        Total wasted on expired items
                      </div>
                      <div className="font-serif text-[24px] font-medium text-critical tabular-nums">
                        ${realWasteData.totalWasted.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {realWasteData.worstItem && (
                    <p className="font-serif text-[13px] italic text-ink-muted mb-3">
                      Most wasted: <span className="font-medium not-italic text-ink">{realWasteData.worstItem}</span>
                    </p>
                  )}

                  {realWasteData.wastedItems.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted mb-2">
                        Recently wasted
                      </div>
                      <div className="rounded-[14px] border border-border-sand overflow-hidden">
                        {realWasteData.wastedItems.slice(0, 3).map((event, idx) => (
                          <div
                            key={event.id}
                            className={`flex items-center justify-between px-4 py-3 bg-cream-warm ${
                              idx !== Math.min(realWasteData.wastedItems.length, 3) - 1
                                ? 'border-b border-border-hairline' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[18px] flex-shrink-0">
                                {event.pantry_items?.emoji ?? '📦'}
                              </span>
                              <span className="font-serif text-[14px] font-medium text-ink truncate">
                                {event.pantry_items?.name ?? 'Unknown'}
                              </span>
                            </div>
                            <span className="font-serif text-[14px] font-medium text-terracotta flex-shrink-0 tabular-nums">
                              ${(event.wasted_dollars ?? event.price_paid ?? 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-2">
                  <Calendar className="h-6 w-6 text-ink-muted mx-auto mb-2" strokeWidth={1.6} />
                  <p className="font-serif text-[15px] font-medium text-ink">
                    No expiry data <em className="italic">yet</em>.
                  </p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-1 leading-[1.4]">
                    Add expiration dates to items to track real waste.
                  </p>
                </div>
              )}
            </div>

            <AnnualProjectionSection
              annualProjection={annualProjection}
              totalTracked={realWasteData.totalTracked}
              isLoading={eventsLoading}
            />

            {/* Budget Card — separate component, passes through */}
            <BudgetCard
              summary={budgetSummary}
              budget={budget ?? null}
              period={budgetPeriod}
              lastMonthSpend={lastMonthSpend}
              monthOverMonthDelta={monthOverMonthDelta}
              onSetBudget={setBudget}
              onViewDetails={() => navigate('/budget-details')}
            />

            {/* Price History */}
            <div className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-[14px] w-[14px] text-ink-muted" strokeWidth={1.8} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Price history
                </div>
              </div>

              {priceHistoryStats.items.length === 0 ? (
                <div className="text-center py-2">
                  <p className="font-serif text-[15px] font-medium text-ink">
                    No price history <em className="italic">yet</em>.
                  </p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-1 leading-[1.4]">
                    Log prices when you confirm items to see how costs change.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 rounded-[12px] bg-critical-bg text-center px-3 py-3">
                      <div className="font-serif text-[22px] font-medium text-critical leading-none tabular-nums">
                        {priceHistoryStats.risingCount}
                      </div>
                      <div className="font-serif text-[11px] italic text-critical mt-1">rising</div>
                    </div>
                    <div className="flex-1 rounded-[12px] bg-ok-bg text-center px-3 py-3">
                      <div className="font-serif text-[22px] font-medium text-forest leading-none tabular-nums">
                        {priceHistoryStats.fallingCount}
                      </div>
                      <div className="font-serif text-[11px] italic text-forest mt-1">falling</div>
                    </div>
                  </div>

                  {priceHistoryStats.biggestIncrease && (
                    <div className="rounded-[12px] bg-cream-warm border border-border-sand p-3 mb-4">
                      <div className="font-serif text-[11px] italic text-ink-muted">Biggest price increase</div>
                      <p className="font-serif text-[14px] font-medium text-ink mt-0.5">
                        {priceHistoryStats.biggestIncrease.emoji} {priceHistoryStats.biggestIncrease.name}
                        {' '}<span className="text-terracotta">up {priceHistoryStats.biggestIncrease.pct.toFixed(0)}%</span>
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => navigate('/price-history')}
                    className="w-full rounded-[14px] border border-border-sand bg-cream py-2.5 text-[13px] font-semibold text-forest"
                  >
                    View all price history →
                  </button>
                </>
              )}
            </div>

            {/* Store Price Tracker */}
            <div className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="flex items-center gap-2 mb-4">
                <StoreIcon className="h-[14px] w-[14px] text-ink-muted" strokeWidth={1.8} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Store price tracker
                </div>
              </div>

              {storePricesData.length === 0 ? (
                <div className="text-center py-2">
                  <p className="font-serif text-[15px] font-medium text-ink">
                    No prices <em className="italic">tracked yet</em>.
                  </p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-1 leading-[1.4]">
                    Save a store name when confirming items to compare.
                  </p>
                </div>
              ) : storeOpportunities.length === 0 ? (
                <div className="text-center py-2">
                  <p className="font-serif text-[14px] italic text-ink-muted">
                    {storePriceComparisons.length} item{storePriceComparisons.length !== 1 ? 's' : ''} tracked.
                  </p>
                  <p className="font-serif text-[12px] italic text-ink-muted/70 mt-1 leading-[1.4]">
                    Log prices at 2+ stores per item to see savings.
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-[14px] bg-ok-bg p-4 mb-3">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-forest mb-1">
                      Potential monthly saving
                    </div>
                    <div className="font-serif text-[32px] font-medium text-forest leading-none tabular-nums">
                      ${totalStoreSaving.toFixed(2)}
                    </div>
                    <p className="font-serif text-[12px] italic text-forest/80 mt-1">
                      across {storeOpportunities.length} item{storeOpportunities.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {topOpportunity && (
                    <div className="rounded-[14px] bg-cream-warm border border-border-sand p-3 mb-3 flex items-start gap-3">
                      <span className="text-[22px] leading-none pt-0.5 flex-shrink-0">
                        {topOpportunity.item.itemEmoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-[14px] font-medium text-ink truncate">
                          {topOpportunity.item.itemName}
                        </p>
                        <p className="text-[12px] font-semibold text-forest mt-0.5">
                          ${topOpportunity.item.stores[0].avgPrice.toFixed(2)} at {topOpportunity.cheaperStore}
                        </p>
                        <p className="font-serif text-[11px] italic text-ink-muted">
                          vs ${(topOpportunity.item.stores[0].avgPrice + topOpportunity.priceDiff).toFixed(2)} at {topOpportunity.currentStore}
                        </p>
                        <p className="font-serif text-[11px] italic text-ink-muted mt-0.5">
                          Save ${topOpportunity.priceDiff.toFixed(2)} per purchase
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              <button
                onClick={() => navigate('/store-prices')}
                className="w-full rounded-[14px] border border-border-sand bg-cream py-2.5 text-[13px] font-semibold text-forest"
              >
                {storeOpportunities.length > 0 ? 'See all opportunities →' : 'Open price tracker →'}
              </button>
            </div>

            {/* Year in Review */}
            <button
              onClick={() => navigate('/year-review')}
              className="rounded-[18px] border border-border-sand bg-forest text-forest-ink text-left p-6 transition active:brightness-110"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-forest-ink/60 mb-1">
                    Year in Review
                  </div>
                  <div className="font-serif text-[22px] font-normal leading-[1.1] tracking-[-0.015em]">
                    Your <em className="italic">{new Date().getFullYear()}</em> story
                  </div>
                </div>
                <ChefHat className="h-5 w-5 text-forest-ink/70 flex-shrink-0 mt-1" strokeWidth={1.6} />
              </div>

              <p className="font-serif text-[13px] italic text-forest-ink/75 leading-[1.45] mb-4">
                Your full grocery year — spending, savings, waste, and biggest wins.
              </p>

              <div className="inline-flex items-center gap-2 rounded-[10px] bg-forest-ink/10 px-3 py-2">
                <span className="text-[12px] font-semibold">View your year</span>
                <span className="text-forest-ink/70">→</span>
              </div>
            </button>

          </div>
        </PremiumGate>

      </div>
    </main>
  )
}
